import {
  createView,
  deleteSavedViews,
  exportCampaignsAsCsv,
  exportDestinationsAsCsv,
  exportEventReportAsCsv,
  getAllMetricsForCampaigns,
  getCampaigns,
  getInsightsFilters,
  getPreviewThumbnails,
  getSavedView,
  getSavedViews,
  getSegments,
  getSegmentsV2,
  getSortedCampaignIdsByMultiMediumMetric,
  getTimeSeriesData,
  getXMedAggStatsForTimeRange,
  setInsightsFilters,
  updateView,
} from 'api/MessagingInsightsAPI';
import { getPDFReport } from 'api/ProductAnalyticsAPI';
import { addNegativeToast, addPositiveToast, removeToastKey } from 'modules/toastContainer';
import { push } from 'redux-first-history';
import { call, put, takeLatest } from 'redux-saga/effects';
import { addToRootSaga } from 'store';
import { BoldSpan } from './Styled';
import * as actionTypes from './actionTypes';
import * as actions from './actions';
import { getProcessedMetricsForCampaign } from './utils';

export function* fetchCampaignsSaga({ queryData }) {
  try {
    const campaignsResponse = yield call(getCampaigns, queryData);
    yield put(actions.fetchCampaignsResponse(campaignsResponse?.data?.campaigns));
  } catch (error) {
    yield put(actions.fetchCampaignsError(error.message));
  }
}

export function* fetchAllMetricsForCampaigns({ queryData }) {
  try {
    const allMetricsResponse = yield call(getAllMetricsForCampaigns, queryData);
    const metricsForCampaignsResp = allMetricsResponse?.data?.campaignLevelMetrics || [];
    const processedMetricResp = getProcessedMetricsForCampaign(metricsForCampaignsResp);
    yield put(actions.fetchAllMetricsForCampaignsResponse(processedMetricResp));
  } catch (error) {
    yield put(actions.fetchAllMetricsForCampaignsError(error.message));
  }
}

export function* exportReportAsCSV({ reportData, callback }) {
  const toastKey = 'eventCsvExportStart';
  try {
    yield put(
      addPositiveToast('Your CSV file is being generated...', {
        key: toastKey,
        noDelay: true,
      }),
    );
    const { data } = yield call(exportEventReportAsCsv, reportData);
    yield put(actions.exportReportDataAsCsvResponse(data));
    yield removeToastKey(toastKey);
    yield put(
      addPositiveToast('Your CSV file has been successfully generated.', {
        key: 'eventCsvExportSuccess',
      }),
    );
    callback(data);
  } catch (error) {
    const errorToastKey = 'eventCsvExportError';
    yield removeToastKey(toastKey);
    yield put(actions.exportReportDataAsCsvError(error.message));
    yield put(
      addNegativeToast('Error exporting CSV. Please try again.', {
        key: errorToastKey,
        noDelay: true,
      }),
    );
  }
}

export function* fetchXMedAggStatsForTimeRange({ queryData }) {
  try {
    const overallPerformanceResponse = yield call(getXMedAggStatsForTimeRange, queryData);
    yield put(actions.recieveXMedAggStatsForTimeRange(overallPerformanceResponse.data));
  } catch (error) {
    yield put(actions.recieveXMedAggStatsForTimeRangeError(error.message));
  }
}

export function* fetchTimeSeriesData({ queryData }) {
  try {
    const overallPerformanceResponse = yield call(getTimeSeriesData, queryData);
    yield put(actions.fetchTimeSeriesDataSuccess(overallPerformanceResponse.data));
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : `${e}`;
    yield put(actions.fetchTimeSeriesDataFailure(errorMessage));
  }
}

export function* fetchBucketMetrics(queryData, successAction, failureAction) {
  try {
    const nonMetricResponse = yield call(getXMedAggStatsForTimeRange, queryData);
    yield put(successAction(nonMetricResponse.data));
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : `${e}`;
    yield put(failureAction(errorMessage));
  }
}

export function* fetchSegments({ queryData }) {
  try {
    const segmentsResponse = yield call(getSegments, queryData);
    yield put(actions.recieveSegmentsResponse(segmentsResponse.data));
  } catch (error) {
    yield put(actions.recieveSegmentsError(error.message));
  }
}

export function* fetchSegmentsV2({ queryData }) {
  try {
    const segmentsResponse = yield call(getSegmentsV2, queryData);
    yield put(actions.fetchSegmentsV2Response(segmentsResponse.data));
  } catch (error) {
    const segmentLimitErrorMsg = error?.response?.data?.message;
    yield put(actions.fetchSegmentsV2Error(segmentLimitErrorMsg || error.message));
  }
}

export function* getInsightsFiltersSaga({ queryData }) {
  try {
    const response = yield call(getInsightsFilters, queryData);
    yield put(actions.getInsightsFiltersResponse(response.data));
  } catch (error) {
    yield put(actions.getInsightsFiltersError(error.message));
  }
}

export function* fetchPreviewThumbnails({ queryData }) {
  try {
    const response = yield call(getPreviewThumbnails, queryData);
    yield put(actions.getPreviewThumbnailsResponse(response.data));
  } catch (error) {
    yield put(actions.getPreviewThumbnailsError(error.message));
  }
}

export function* setInsightsFiltersSaga({ postData }) {
  try {
    const response = yield call(setInsightsFilters, postData);
    yield put(actions.setInsightsFiltersResponse(response.data));
    yield put(addPositiveToast('Default filters saved'));
  } catch (error) {
    yield put(actions.setInsightsFiltersError(error.message));
    yield put(addNegativeToast('Error saving filters, please try again.'));
  }
}

export function* csvExportToast() {
  try {
    yield put(addPositiveToast('Exporting campaign metrics'));
  } catch (error) {
    console.log(error); // if this saga fails, it's the toast itself that's failing. Just going to console log it.
  }
}

export function* exportCampaignsAsCsvSaga({ queryData, callback }) {
  const toastKey = 'messagingInsightsCsvExportStart';
  try {
    yield put(
      addPositiveToast('Your CSV file is being generated...', {
        key: toastKey,
        noDelay: true,
      }),
    );
    const { data } = yield call(exportCampaignsAsCsv, queryData);
    yield put(actions.exportCampaignsAsCsvResponse(data));
    callback(data);
    yield removeToastKey(toastKey);
    yield put(
      addPositiveToast('Your CSV file has been successfully generated.', {
        key: 'messagingInsightsCsvExportSuccess',
      }),
    );
  } catch (error) {
    yield removeToastKey(toastKey);
    yield put(actions.exportCampaignsAsCsvError());
    yield put(
      addNegativeToast('Error exporting CSV, please try again.', {
        key: 'messagingInsightsCsvExportError',
        noDelay: true,
      }),
    );
  }
}

export function* exportDestinationsAsCsvSaga({ queryData, callback }) {
  const toastKey = 'messagingInsightsDestinationCsvExportStart';
  try {
    yield put(
      addPositiveToast('Your CSV file is being generated...', {
        key: toastKey,
        noDelay: true,
      }),
    );
    const { data } = yield call(exportDestinationsAsCsv, queryData);
    yield put(actions.exportDestinationsAsCsvResponse(data));
    callback(data);
    yield removeToastKey(toastKey);
    yield put(
      addPositiveToast('Your CSV file has been successfully generated.', {
        key: 'messagingInsightsDestinationCsvExportSuccess',
      }),
    );
  } catch (error) {
    yield removeToastKey(toastKey);
    yield put(actions.exportDestinationsAsCsvError());
    yield put(
      addNegativeToast('Error exporting CSV, please try again.', {
        key: 'messagingInsightsDestinationCsvExportError',
        noDelay: true,
      }),
    );
  }
}

export function* fetchSortedCampaignIdsByMultiMediumMetricSaga({ queryData }) {
  try {
    const { data } = yield call(getSortedCampaignIdsByMultiMediumMetric, queryData);
    yield put(actions.fetchSortedCampaignIdsByMultiMediumMetricResponse(data));
  } catch (error) {
    yield put(actions.fetchSortedCampaignIdsByMultiMediumMetricError());
    yield put(addNegativeToast('Error fetching sorted campaigns, please try again.'));
  }
}
export function* fetchSavedView({ viewId }) {
  try {
    const { data } = yield call(getSavedView, viewId);
    yield put(actions.fetchSavedViewResponse(data));
  } catch (error) {
    const {
      response: { status },
    } = error;
    if (status === 403) {
      yield put(actions.fetchSavedViewError(403));
    } else if (status === 404) {
      yield put(actions.fetchSavedViewError(404));
      yield put(addNegativeToast(`View ${viewId} not found, please try a different view.`));
    } else {
      yield put(addNegativeToast('Error fetching saved view, please try again.'));
    }
  }
}

export function* createViewSaga({ view, callback }) {
  try {
    const { data } = yield call(createView, view);
    yield put(actions.createViewResponse({ id: data?.viewId, ...view }));
    yield put(
      addPositiveToast(
        <span>
          <b>{view.name}</b> created successfully
        </span>,
      ),
    );
    yield put(push(`/messagingInsights/views/${data?.viewId}`));
    try {
      callback();
    } catch (error) {
      if (error?.response?.status !== 404) {
        yield put(addNegativeToast('Error resetting dropdown, please refresh.'));
      }
    }
  } catch (error) {
    yield put(actions.createViewError());
    yield put(addNegativeToast('Error creating view, please try again.'));
  }
}

export function* updateViewSaga({ view, callback }) {
  const { id, ...payload } = view;
  try {
    yield call(updateView, id, payload);
    yield put(actions.updateViewResponse(payload));
    yield put(addPositiveToast('Changes saved'));
    try {
      if (callback) {
        callback();
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        yield put(addNegativeToast('Error resetting dropdown, please refresh.'));
      }
    }
  } catch (error) {
    yield put(actions.updateViewError());
    yield put(addNegativeToast('Changes could not be saved'));
  }
}

export function* fetchSavedViewsInfiniteScroll({ queryParams }) {
  try {
    const { data } = yield call(getSavedViews, queryParams);
    yield put(actions.fetchSavedViewsInfiniteScrollResponse(data));
  } catch (error) {
    yield put(actions.fetchSavedViewsInfiniteScrollError());
  }
}

export function* fetchRecentSavedViews({ queryParams }) {
  try {
    const { data } = yield call(getSavedViews, queryParams);
    yield put(actions.fetchRecentSavedViewsResponse(data));
  } catch (error) {
    yield put(actions.fetchRecentSavedViewsError());
    yield put(addNegativeToast('Error fetching recently visited saved views, please try again.'));
  }
}

export function* fetchProjectDefaultView() {
  try {
    const queryString = 'projectDefaultOnly=true&pageSize=1&page=1';
    const { data } = yield call(getSavedViews, queryString);
    yield put(actions.fetchProjectDefaultViewResponse(data));
  } catch (error) {
    // 404s are expected for users without a project default
    yield put(actions.fetchProjectDefaultViewError());
    if (error?.response?.status !== 404) {
      yield put(addNegativeToast('Error fetching project default view, please try again.'));
    }
  }
}

export function* deleteSavedViewsSaga({ view, callback }) {
  try {
    yield call(deleteSavedViews, view.id);
    yield put(
      addPositiveToast(
        <>
          <BoldSpan>{view.name}</BoldSpan> is deleted.
        </>,
      ),
    );
    try {
      callback();
    } catch (error) {
      console.log(error);
    }
  } catch (error) {
    yield put(addNegativeToast('Error deleting saved view, please try again.'));
  }
}

export function* exportPDFReportSaga({ queryData }) {
  try {
    const response = yield call(getPDFReport, queryData);

    // Create a Blob from the binary response
    const blob = new Blob([response], { type: 'application/pdf' });

    // Generate a URL for the Blob
    const url = window.URL.createObjectURL(blob);

    // Dispatch the Blob URL to Redux for reuse in the UI
    yield put(actions.fetchPDFReportResponse(url));

    // Automatically open the PDF in a new tab
    window.open(url, '_blank');
  } catch (err) {
    const error = `Failed to generate the PDF report due to ${err?.message}`;
    yield put(actions.fetchPDFReportError({ error }));
  }
}

export function handleResetPdfReport({ pdfReportUrl }) {
  if (pdfReportUrl) {
    URL.revokeObjectURL(pdfReportUrl);
  }
}

addToRootSaga([
  takeLatest(actionTypes.FETCH_CAMPAIGNS_REQUEST, fetchCampaignsSaga),
  takeLatest(actionTypes.CSV_EXPORT, csvExportToast),
  takeLatest(
    actionTypes.FETCH_X_MED_AGG_STATS_FOR_TIME_RANGE_REQUEST,
    fetchXMedAggStatsForTimeRange,
  ),
  takeLatest(actionTypes.FETCH_TIME_SERIES_DATA_FOR_TIME_RANGE_REQUEST, fetchTimeSeriesData),
  takeLatest(actionTypes.FETCH_LOCATION_DATA_FOR_TIME_RANGE_REQUEST, ({ payload }) =>
    fetchBucketMetrics(payload, actions.fetchLocationDataSuccess, actions.fetchLocationDataFailure),
  ),
  takeLatest(actionTypes.FETCH_DEVICE_DATA_FOR_TIME_RANGE_REQUEST, ({ payload }) =>
    fetchBucketMetrics(payload, actions.fetchDeviceDataSuccess, actions.fetchDeviceDataFailure),
  ),
  takeLatest(actionTypes.EXPORT_INSIGHT_REPORT_DATA_AS_CSV_REQUEST, exportReportAsCSV),
  takeLatest(actionTypes.FETCH_SEGMENTS_REQUEST, fetchSegments),
  takeLatest(actionTypes.FETCH_SEGMENTSV2_REQUEST, fetchSegmentsV2),
  takeLatest(actionTypes.GET_PREVIEW_THUMBNAILS_REQUEST, fetchPreviewThumbnails),
  takeLatest(
    [actionTypes.GET_INSIGHTS_FILTERS_REQUEST, actionTypes.SET_INSIGHTS_FILTERS_RESPONSE],
    getInsightsFiltersSaga,
  ),
  takeLatest(actionTypes.SET_INSIGHTS_FILTERS_REQUEST, setInsightsFiltersSaga),
  takeLatest(actionTypes.EXPORT_DESTINATIONS_AS_CSV_REQUEST, exportDestinationsAsCsvSaga),
  takeLatest(actionTypes.EXPORT_CAMPAIGNS_AS_CSV_REQUEST, exportCampaignsAsCsvSaga),
  takeLatest(
    actionTypes.FETCH_SORTED_CAMPAIGN_IDS_BY_MULTI_MEDIUM_METRIC_REQUEST,
    fetchSortedCampaignIdsByMultiMediumMetricSaga,
  ),
  takeLatest(actionTypes.FETCH_ALL_METRICS_FOR_CAMPAIGNS_REQUEST, fetchAllMetricsForCampaigns),
  takeLatest(actionTypes.CREATE_VIEW_REQUEST, createViewSaga),
  takeLatest(actionTypes.FETCH_SAVED_VIEW_REQUEST, fetchSavedView),
  takeLatest(actionTypes.UPDATE_VIEW_REQUEST, updateViewSaga),
  takeLatest(actionTypes.FETCH_SAVED_VIEWS_INFINITE_SCROLL_REQUEST, fetchSavedViewsInfiniteScroll),
  takeLatest(actionTypes.FETCH_RECENT_SAVED_VIEWS_REQUEST, fetchRecentSavedViews),
  takeLatest(actionTypes.FETCH_PROJECT_DEFAULT_VIEW_REQUEST, fetchProjectDefaultView),
  takeLatest(actionTypes.DELETE_SAVED_VIEWS_REQUEST, deleteSavedViewsSaga),
  takeLatest(actionTypes.FETCH_PDF_REPORT_REQUEST, exportPDFReportSaga),
  takeLatest(actionTypes.RESET_PDF_REPORT, handleResetPdfReport),
]);
